import { ETAB_ROUTES } from "@etab/config";
import { isMobile, isNative } from "@core/plugins/mobile";
import { Logger } from "@core/helpers/logger.helpers";
import { PurchaselyManager } from "@etab/managers/purchasely.manager";
import { SupervisorManager, StartupManager, MultiAccountManager } from "@etab/managers";
import { SupervisorModel, ISupervisor, SupervisedEtabModel, ISupervisedEtab } from "@core/models";
import preloadImages from "@etab/assets";
import { API_NAMESPACE_ETAB, ROLE } from "@core/constants";
import { UserStorageManager } from "@core/managers/user-storage.manager";
import { BatchManager } from "@core/plugins/batch/batch.manager";
import { KochavaManager } from "@core/managers/kochava.manager";
import { CORE_ACTION_FEATURE_TOGGLES_FETCH_PRIVATE, CORE_ACTION_FEATURE_TOGGLES_FETCH_PUBLIC } from "@core/store/types";
import { TrackingManager } from "@core/managers/tracking.manager";
import { PosthogManager } from "@core/managers/posthog.manager";
import { useFeatureTogglesStore } from "@core/store/modules/featureToggles";
import DeepLinkService from "@core/managers/deeplink.manager";
const homeRoute = { name: ETAB_ROUTES.loading.name };
const loginRoute = { name: ETAB_ROUTES.auth.login.name };
const multiAccount = { name: ETAB_ROUTES.account.multiAccount.name };
/**
 * Everything the app needs to do before the first component is rendered
 */
export async function preStartupHandler({ router }) {
  try {
    // preload the images for better reactivity
    await initializeBasicServices();
    await initializeMobileServices({ router });
    await initializeWebServices({ router });
    await getFeatureToggle();
    if (PurchaselyManager.isIAPActivated()) {
      PurchaselyManager.start({ router });
    }
  } catch (error) {
    // fail silently
    Logger.error(error);
  }
}

async function initializeBasicServices() {
  await preloadImages();
  await handleMultiAccountMode();
  TrackingManager.initialize();
  PosthogManager.init();
}
async function initializeMobileServices({ router }) {
  if (!isNative) return;
  const deeplinkManager = DeepLinkService.getInstance({ router, defaultRouteName: ETAB_ROUTES.dashboard.root.name });
  await deeplinkManager.initialize();
  deeplinkManager.addAuthHandler(async (data) => {
    await handleAutoAuth({ url: data.url, router });
  });
  KochavaManager.init();
  BatchManager.initialize({ router });
}
async function initializeWebServices({ router }) {
  if (isMobile) return;
  await handleAutoAuth({ router });
  await handleSupervisorModeInitIfNeeded();
}
async function handleAutoAuth({ url = window.location.href, router } = {}) {
  const temporaryToken = await StartupManager.getTemporaryTokenFromUrl({ url });
  if (temporaryToken) {
    await attemptToLoginAndNavigate({ temporaryToken, url, router });
  }
}
async function attemptToLoginAndNavigate({ temporaryToken, url, router } = {}) {
  if (temporaryToken) {
    try {
      const userHasBeenAuth = await StartupManager.authenticateFromTemporaryToken({ temporaryToken });
      const user = await UserStorageManager.getUser();
      const isMasterEtab = user.role[0] === ROLE.MASTER;
      if (userHasBeenAuth) {
        if (isMasterEtab) {
          router.push(multiAccount).catch((suppressedError) => {});
        } else {
          await handleSupervisorMode({ url });
          router.push(homeRoute).catch((suppressedError) => {});
        }
      }
    } catch (error) {
      Logger.error("Login failed", error);
      router.push(loginRoute).catch((suppressedError) => {});
    }
  }
}
async function handleSupervisorModeInitIfNeeded() {
  // check supervisor mode and init if needed
  if (!SupervisorManager.hasStarted) {
    await SupervisorManager.initSupervisorMode();
  }
}
async function handleSupervisorMode({ url }) {
  const etabId = await SupervisorManager.getEtabIdFromUrl({ url });
  const supervisorId = await SupervisorManager.getSupervisorIdFromUrl({ url });
  if (etabId && supervisorId) {
    const supervisor = new SupervisorModel({
      [ISupervisor.etabId]: etabId,
      [ISupervisor.supervisorId]: supervisorId,
    });
    await SupervisorManager.startSupervisorMode({ supervisor, shouldSaveData: true });
  }
  return;
}
async function handleMultiAccountMode() {
  const multiAccountData = await UserStorageManager.getSupervisorData();
  if (multiAccountData) {
    const multiAccountSupervisor = new SupervisedEtabModel({
      [ISupervisedEtab.masterEtabId]: multiAccountData[ISupervisedEtab.masterEtabId],
      [ISupervisedEtab.supervisedEtabId]: multiAccountData[ISupervisedEtab.supervisedEtabId],
    });
    MultiAccountManager.startSupervisedEtabMode({ multiAccountSupervisor, shouldSaveData: true });
  }
}
async function getFeatureToggle() {
  const featureTogglesStore = useFeatureTogglesStore();
  await featureTogglesStore[CORE_ACTION_FEATURE_TOGGLES_FETCH_PUBLIC]();
  await featureTogglesStore[CORE_ACTION_FEATURE_TOGGLES_FETCH_PRIVATE](API_NAMESPACE_ETAB);
}
